import { ReactNode, useState } from 'react';
import {
  ArrowUpFromLine,
  Ban,
  CircleAlert,
  Ellipsis,
  LoaderCircle,
  RefreshCw,
  SquareArrowOutUpRight,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import {
  ActionMenuItem,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@tinct/web-components';
import { cn } from '@tinct/core-utils';
import { EmbeddedPage, PageStatus, ReadCampaign } from '@tinct/core-api-models';
import { getEffectiveStatus, getPageDisplayIdentity } from '../../../pages/shared';
import { getPageActionCapabilities } from '../../../pages/shared/get-page-action-capabilities';
import { PAGE_STATUS_CONFIG } from '../../../pages/shared/page-status-config';
import { PAGE_OUTDATED_STATUS_CONFIG } from '../../../pages/shared/page-outdated-status-config';

type PageItemProps = {
  page: EmbeddedPage;
  /** Gates Preview (campaign live + snippet detected). */
  campaign?: Pick<ReadCampaign, 'status' | 'snippetStatus'> | null;
  isSelected?: boolean;
  /** Icon-rail mode: render only the logo tile. */
  collapsed?: boolean;
  disableHover?: boolean;
  onClick?: (account: EmbeddedPage) => void;
  onPreview?: (page: EmbeddedPage) => void;
  onRetry?: (page: EmbeddedPage) => void;
  onToggleExclude?: (page: EmbeddedPage) => void;
  onPublish?: (page: EmbeddedPage) => void;
  onUnpublish?: (page: EmbeddedPage) => void;
};

const CLICKABLE_STATUSES = new Set<PageStatus>([
  'ready',
  'published',
  'error',
  'excluded',
  'generating',
  'unpublished',
  'queued',
]);

const DIMMED_STATUSES = new Set<PageStatus>([
  'generating',
  'queued',
  'draft',
  'waiting',
  'unpublished',
]);

// Statuses with a "…" menu. `unpublished` is here so an unpublished variant is
// not a dead end: it can be published back or excluded from the panel.
const ACTIONABLE_STATUSES = new Set<PageStatus>([
  'ready',
  'excluded',
  'published',
  'error',
  'unpublished',
]);

// Ad pages have their own generation path (the ad-pages endpoint, reached via
// regenerate) that always requeues a fresh job — so a pending ad variant gets
// a manual "Generate" action. It covers both a never-started page and a job
// orphaned in `queued` by a backend restart, which otherwise deadlocks: the
// queued job disables "Generate all" and nothing else can fire generation.
const AD_GENERATABLE_STATUSES = new Set<PageStatus>(['draft', 'waiting', 'queued']);

/** Compact status marker: colored dot + label, sized for the dark panel rows. */
const DotTag = ({ dotClass, label }: { dotClass?: string; label: ReactNode }) => (
  <span className="flex items-center gap-1 text-xs text-muted-foreground">
    {dotClass && (
      <span className="flex size-3 shrink-0 items-center justify-center">
        <span className={cn('size-2 rounded-full', dotClass)} />
      </span>
    )}
    <span className="truncate">{label}</span>
  </span>
);

export const PageItem = ({
  page,
  campaign,
  isSelected,
  collapsed,
  disableHover,
  onClick,
  onPreview,
  onRetry,
  onToggleExclude,
  onPublish,
  onUnpublish,
}: PageItemProps) => {
  // Ad pages have no lead/company — the identity helper names them by their
  // ads campaign instead of silently dropping them from the panel.
  const company = page.lead?.company ?? null;
  const { label, targetType } = getPageDisplayIdentity(page);

  const isExcluded = page.status === 'excluded';
  const isError = page.status === 'error';
  const effectiveStatus = getEffectiveStatus(page);
  const isGenerating = effectiveStatus === 'generating';
  const isClickable = CLICKABLE_STATUSES.has(effectiveStatus);
  const isDimmed = DIMMED_STATUSES.has(effectiveStatus);
  const isAdGeneratable =
    targetType === 'ad_campaign' && AD_GENERATABLE_STATUSES.has(effectiveStatus);
  const hasActions = ACTIONABLE_STATUSES.has(effectiveStatus) || isAdGeneratable;
  // Shared with the dashboard menus: which publication action applies, and
  // whether Preview is allowed (the embedded page has no URL, so Copy URL is
  // not offered here).
  const capabilities = getPageActionCapabilities(page, campaign);
  const logoUrl = company?.logo?.url;
  // Tracked per URL so a page whose logo gets fixed recovers without a remount.
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);
  const showLogo = !!logoUrl && logoUrl !== failedLogoUrl;
  const statusConfig = PAGE_STATUS_CONFIG[effectiveStatus];
  const outdatedConfig =
    page.outdatedStatus && page.outdatedStatus !== 'none'
      ? PAGE_OUTDATED_STATUS_CONFIG[page.outdatedStatus]
      : null;

  return (
    <div
      className={cn(
        'group flex h-[52px] w-full cursor-pointer items-center gap-3 overflow-hidden rounded-xl p-2',
        isSelected
          ? 'bg-indigo-900 ring-[1.5px] ring-inset ring-primary'
          : disableHover
            ? 'opacity-70'
            : 'hover:bg-accent/50'
      )}
      onClick={() => {
        if (onClick && isClickable) {
          onClick(page);
        }
      }}
    >
      {/* Logo */}
      {/* Logos sit on white; the generating slot is a dark card so the spinner
          reads as part of the (dark) panel rather than a blank thumbnail. */}
      <div
        className={cn(
          'relative size-9 shrink-0 overflow-hidden rounded-md border border-border-subtle',
          isGenerating ? 'bg-card' : 'bg-white'
        )}
      >
        {isGenerating ? (
          <div className="flex size-full items-center justify-center">
            <LoaderCircle className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : showLogo ? (
          <>
            <img
              src={logoUrl}
              alt={label}
              className="absolute size-full object-cover"
              onError={() => setFailedLogoUrl(logoUrl)}
            />
            {isDimmed && <div className="absolute inset-0 bg-black mix-blend-color" />}
          </>
        ) : (
          <div className="flex size-full items-center justify-center bg-muted text-xs font-medium text-muted-foreground">
            {label.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      {/* Info */}
      {!collapsed && (
      <div className="flex min-w-0 flex-1 flex-col items-start justify-center self-stretch">
        <p
          className={`w-full truncate text-sm font-medium ${
            isDimmed ? 'text-muted-foreground' : 'text-foreground'
          }`}
        >
          {label}
        </p>
        <div className="flex w-full items-center gap-3">
          <DotTag
            dotClass={statusConfig.hideDot ? undefined : statusConfig.dotColor}
            label={statusConfig.label}
          />
          {/* Same red alert as PageOutdatedStatusBadge (design: Item / Tag, red-500),
              in the compact dot-row layout of the panel. */}
          {outdatedConfig && (
            <span className="flex items-center gap-1 text-xs text-red-500">
              <CircleAlert className="size-3 shrink-0" />
              <span className="truncate">{outdatedConfig.label}</span>
            </span>
          )}
        </div>
      </div>
      )}

      {/* Actions */}
      {!collapsed && hasActions && (
        <div className="flex shrink-0 items-center self-end self-stretch opacity-0 group-hover:opacity-100">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={(e) => e.stopPropagation()}
              >
                <Ellipsis className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            {/* The content is portaled — out of the dark panel, so it carries
                the theme itself — but React events still bubble to the row,
                which would select it: stop them once here. */}
            <DropdownMenuContent align="start" className="dark" onClick={(e) => e.stopPropagation()}>
              {!isAdGeneratable && onPreview && (
                <ActionMenuItem
                  onSelect={() => onPreview(page)}
                  blockedReason={capabilities.preview.reason}
                >
                  <SquareArrowOutUpRight className="size-4" />
                  Preview
                </ActionMenuItem>
              )}
              {capabilities.publish.visible && (
                <DropdownMenuItem onSelect={() => onPublish?.(page)}>
                  <ArrowUpFromLine className="size-4" />
                  Publish
                </DropdownMenuItem>
              )}
              {(capabilities.exclude.visible || capabilities.include.visible) && (
                <DropdownMenuItem onSelect={() => onToggleExclude?.(page)}>
                  {isExcluded ? <ToggleRight className="size-4" /> : <ToggleLeft className="size-4" />}
                  {isExcluded ? 'Include in publishing' : 'Exclude from publishing'}
                </DropdownMenuItem>
              )}
              {capabilities.unpublish.visible && (
                <DropdownMenuItem onSelect={() => onUnpublish?.(page)}>
                  <Ban className="size-4" />
                  Unpublish
                </DropdownMenuItem>
              )}
              {isError && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => onRetry?.(page)}>
                    <RefreshCw className="size-4" />
                    Retry
                  </DropdownMenuItem>
                </>
              )}
              {isAdGeneratable && (
                <DropdownMenuItem onSelect={() => onRetry?.(page)}>
                  <RefreshCw className="size-4" />
                  Generate now
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
};
